/**
 * @brisa/ui · facelift/club-card/ClubCardFacelift
 *
 * Feature: migracao-facelift-v1 · COMPONENT_MAP §1 #13 (club-card-facelift)
 * Figma origem: node 163:923 — reforma per 179:185 (Bruno 2026-05-14):
 *   - radius xl (32px), drop-shadow stack 5 camadas
 *   - AvatarClube TOP (sem borda branca; shadow no próprio AvatarClube)
 *   - Bottom: VerifiedPartnerBadge ACIMA do nome (não inline com avatar),
 *     nome 1.5rem Archivo Medium, endereço text-inverse-50 com pin icon
 *
 * Sem 'use client' — apresentacional puro (Link é Server-side compat).
 */
import * as React from 'react';
import Image from 'next/image';

import { AvatarClube } from '../avatar-clube/AvatarClube';
import { cn } from '@brisa/ui/lib/utils';

export interface ClubCardListItem {
  slug: string;
  name: string;
  neighborhood?: string | null;
  cover_url?: string | null;
  avatar_url?: string | null;
  integration_status?: 'partner' | 'listed' | string;
  vibe?: string;
}

export interface ClubCardFaceliftProps extends React.HTMLAttributes<HTMLElement> {
  club: ClubCardListItem;
  href?: string;
  /**
   * Aplica a drop-shadow stack canônica (5 camadas Figma 163:923).
   * Default `true`. Set `false` quando o card é renderizado em grids densos
   * (ex: catálogo /clubes) onde o filter compose ataca a performance.
   */
  dropShadow?: boolean;
}

// Drop-shadow stack canônico do card (Figma 163:923 — 5 camadas elevação alta)
const CARD_SHADOW =
  '[filter:drop-shadow(0_156px_31px_rgba(0,0,0,0.01))_drop-shadow(0_88px_27px_rgba(0,0,0,0.05))_drop-shadow(0_39px_20px_rgba(0,0,0,0.09))_drop-shadow(0_10px_11px_rgba(0,0,0,0.10))]';

/**
 * Selo "B" parceiro — SVG oficial fornecido pelo PL (verified.svg, node 179:164).
 * Estrela 12-pontas em #FFE400 + letra "B" preta. Posicionado no bottom do
 * card (acima do nome) per 179:185.
 */
function VerifiedPartnerBadge() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 32 32"
      width={32}
      height={32}
      fill="none"
      aria-label="Club parceiro"
      role="img"
      data-slot="partner-badge"
      className="shrink-0"
    >
      <path
        d="M11.4668 30L8.9335 25.7333L4.1335 24.6667L4.60016 19.7333L1.3335 16L4.60016 12.2667L4.1335 7.33333L8.9335 6.26667L11.4668 2L16.0002 3.93333L20.5335 2L23.0668 6.26667L27.8668 7.33333L27.4002 12.2667L30.6668 16L27.4002 19.7333L27.8668 24.6667L23.0668 25.7333L20.5335 30L16.0002 28.0667L11.4668 30Z"
        fill="#FFE400"
      />
      <path
        d="M11.9619 20.4999V11.5559H17.9159C18.3666 11.5559 18.7782 11.6512 19.1509 11.8419C19.5236 12.0239 19.8226 12.2796 20.0479 12.6089C20.2732 12.9382 20.3859 13.3196 20.3859 13.7529C20.3859 14.1862 20.3166 14.5416 20.1779 14.8189C20.0392 15.0962 19.8572 15.3172 19.6319 15.4819C19.4066 15.6379 19.1596 15.7549 18.8909 15.8329V15.8849C19.2029 15.9542 19.4846 16.0756 19.7359 16.2489C19.9959 16.4136 20.2039 16.6476 20.3599 16.9509C20.5159 17.2542 20.5939 17.6442 20.5939 18.1209C20.5939 18.5976 20.4769 19.0136 20.2429 19.3689C20.0089 19.7242 19.6969 20.0016 19.3069 20.2009C18.9169 20.4002 18.4792 20.4999 17.9939 20.4999H11.9619ZM14.8349 18.4199H17.0189C17.2182 18.4199 17.3786 18.3506 17.4999 18.2119C17.6212 18.0732 17.6819 17.8652 17.6819 17.5879C17.6819 17.4579 17.6516 17.3409 17.5909 17.2369C17.5389 17.1242 17.4609 17.0376 17.3569 16.9769C17.2616 16.9162 17.1489 16.8859 17.0189 16.8859H14.8349V18.4199ZM14.8349 15.0399H16.8109C16.9496 15.0399 17.0666 15.0096 17.1619 14.9489C17.2572 14.8796 17.3309 14.7842 17.3829 14.6629C17.4436 14.5416 17.4739 14.3899 17.4739 14.2079C17.4739 14.0172 17.4089 13.8569 17.2789 13.7269C17.1489 13.5882 16.9929 13.5189 16.8109 13.5189H14.8349V15.0399Z"
        fill="black"
      />
    </svg>
  );
}

export function ClubCardFacelift({
  club,
  href,
  className,
  dropShadow = true,
  ...props
}: ClubCardFaceliftProps) {
  const Wrapper = href ? ('a' as const) : ('article' as const);
  const wrapperProps = href ? { href } : {};
  const isPartner = club.integration_status === 'partner';

  return (
    <Wrapper
      className={cn(
        // min-w-0 obrigatório pra cards em CSS Grid — sem ele, items têm
        // min-width: auto e crescem pra fit do nome do club, quebrando a
        // distribuição 1fr equal das colunas (Bruno 2026-05-16).
        'relative block h-[354px] w-full min-w-0 overflow-hidden rounded-[var(--radius-xl)]',
        // Sem hover (Bruno 2026-05-15): mesmo o scale GPU-promoted causava
        // repaint da pipeline drop-shadow stack durante o marquee, comendo
        // performance. Card fica estático no hover.
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        dropShadow && CARD_SHADOW,
        className,
      )}
      data-slot="club-card-facelift"
      {...wrapperProps}
      {...props}
    >
      {/* Photo bg — sem transform interno; o card inteiro escala no hover. */}
      {club.cover_url ? (
        <Image
          src={club.cover_url}
          alt={`Foto de ${club.name}`}
          fill
          className="object-cover"
          sizes="(min-width: 768px) 33vw, 100vw"
        />
      ) : (
        <div className="absolute inset-0 bg-[hsl(var(--surface-dark-solid))]" />
      )}

      {/* Scrim gradient — transparent até 46%, depois pra black no bottom (Figma 163:923).
          Inline gradient (não Tailwind) pra garantir % stop em qualquer build. */}
      <div
        aria-hidden="true"
        className="absolute inset-0"
        style={{
          background:
            'linear-gradient(to bottom, transparent 0%, transparent 46.6%, rgba(0,0,0,0.85) 100%)',
        }}
      />

      {/* Content — flex col justify-between: avatar top, info bottom */}
      <div className="relative flex h-full flex-col justify-between p-6">
        {/* Top — Avatar (com shadow stack próprio, sem borda branca) */}
        <AvatarClube club={club} size="md" />

        {/* Bottom — Badge (acima) + Nome + Endereço */}
        <div className="flex flex-col items-start gap-2 text-[hsl(var(--neutral-white))]" data-slot="body">
          {isPartner ? <VerifiedPartnerBadge /> : null}
          <h3 className="font-body text-3xl font-medium leading-none tracking-tight md:text-2xl">
            {club.name}
          </h3>
          {club.neighborhood ? (
            <p className="flex items-center gap-2 text-base leading-tight text-[hsl(var(--text-inverse-50))] md:text-xs">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width={16}
                height={16}
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
                className="shrink-0"
              >
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                <circle cx="12" cy="10" r="3" />
              </svg>
              {club.neighborhood}
            </p>
          ) : null}
        </div>
      </div>
    </Wrapper>
  );
}
